import { HeatNumberLookup } from "@/features/rbi/common/lookup";
import { PageButton, PageControls } from "../../../common/PageControls";
import { SaveChangesDialog } from "../../../common/Dialogs";
import { useContext } from "react";
import { useCoordinatorWelderProfiles } from "@/queries/welder.queries";
import { useGetProcesses, useSave } from "../queries";
import { useModal } from "@/hooks";
import { WelderContext } from "../reducer";
import { WelderRow } from "@/api/cds/rbi";

export function MainInterface() {
  const { state, updateState } = useContext(WelderContext);
  const { isLoading } = useGetProcesses(state.welds);

  const save = useSave();
  const modal = useModal();
  const welders = useCoordinatorWelderProfiles();

  if (isLoading) {
    return <p>Loading welds...</p>;
  }

  function isGroupRow(row: WelderRow) {
    const index = state.processes.indexOf(row);
    return index === 0 || row.weldId !== state.processes[index - 1].weldId;
  }

  function handleFieldChanged(row: WelderRow, field: keyof WelderRow, value: unknown) {
    const processes = [...state.processes];
    const index = processes.indexOf(row);
    processes[index] = { ...processes[index], [field]: value };

    updateState({
      type: "Processes",
      payload: processes,
    });
  }

  function handleWelderSelected(row: WelderRow, welderId: string) {
    if (welders.data) {
      const matches = welders.data.filter((welder) => welder.welderId === welderId);
      if (matches.length > 0) {
        handleFieldChanged(row, "welder", matches[0]);
      } else {
        handleFieldChanged(row, "welder", null);
      }
    }
  }

  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover text-center text-nowrap">
          <thead>
            <tr>
              <th>Weld No.</th>
              <th>Process</th>
              <th>WPS</th>
              <th>Filler</th>
              <th>Welder ID</th>
              <th>Filler Heat No.</th>
              <th>Made</th>
            </tr>
          </thead>
          <tbody>
            {state.processes.map((row, index) => (
              <tr key={index}>
                <th>{isGroupRow(row) && row.weldNumber}</th>
                <td>{row.process}</td>
                <td>{row.wpsNumber}</td>
                <td>{row.filler}</td>
                <td>
                  <select
                    className="form-select"
                    value={row.welder?.welderId}
                    onChange={(e) => handleWelderSelected(row, e.currentTarget.value)}
                    disabled={row.made}
                  >
                    <option />
                    {welders.data &&
                      welders.data
                        .filter((welder) => welder.badgeNumber)
                        .map((welder) => (
                          <option key={welder.welderId} value={welder.welderId}>
                            {welder.badgeNumber}
                          </option>
                        ))}
                  </select>
                </td>
                <td>
                  <HeatNumberLookup
                    classification={row.filler}
                    value={row.heatNumber}
                    onChange={(e) => handleFieldChanged(row, "heatNumber", e.currentTarget.value)}
                    disabled={row.made}
                  />
                </td>
                <td>
                  <div className="form-check form-switch d-flex justify-content-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={row.made}
                      onChange={(e) => handleFieldChanged(row, "made", e.currentTarget.checked)}
                      disabled={!row.welder || !row.heatNumber}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PageControls showBack>
        <PageButton
          onClick={() => modal.show(<SaveChangesDialog onAccept={() => save.mutate(state.processes)} />)}
          loading={save.isLoading}
        >
          Save and Finish
        </PageButton>
      </PageControls>
    </>
  );
}
